// 구조 공유. 폴링은 같은 값을 매번 새 객체로 파싱해 오므로, 이전 응답과 깊이 같은 부분은 이전 객체를 그대로 둔다 —
// 응답 전체가 같으면 이전 값 자체가 돌아와 `usePoll` 의 상태가 바뀌지 않고 화면을 다시 그리지 않는다.
// 한 줄만 바뀌면 그 줄과 그것을 담은 배열만 새것이고, 나머지 줄은 이전 객체라 `memo` 가 건너뛸 수 있다.
//
// 돌아오는 값은 늘 next 와 깊이 같다 — 이전 것을 쓰는 것은 그 부분이 같을 때뿐이라 낡은 값이 남지 않는다.
// 다시 그리기가 남을 수는 있어도 빠지지는 않는다.
//
// - 들어가 견주는 것은 평범한 객체(프로토타입이 Object.prototype 이거나 null)와 배열뿐이다. 키의 순서는 보지 않는다.
//   그 밖의 객체(Date·Map·클래스)는 속을 알 수 없으므로 새 값이다. 원시값은 Object.is 다.
// - 배열의 원소는 `id`, 없으면 `lease_id`, 그것도 없으면 위치로 짝짓는다 — 앞에 임대가 하나 끼거나 VM 하나가
//   켜지고 꺼져도 나머지 줄의 정체성이 밀리지 않는다. 키는 문자열이나 수일 때만 쓴다.

type Plain = Record<string, unknown>

function plain(v: unknown): v is Plain {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false
  const proto = Object.getPrototypeOf(v) as unknown
  return proto === Object.prototype || proto === null
}

function keyOf(v: unknown, field: 'id' | 'lease_id'): string | number | undefined {
  if (!plain(v)) return undefined
  const k = v[field]
  return typeof k === 'string' || typeof k === 'number' ? k : undefined
}

function shareObject(prev: Plain, next: Plain): Plain {
  const keys = Object.keys(next)
  let same = keys.length === Object.keys(prev).length
  const out = keys.map((k): [string, unknown] => {
    const had = Object.hasOwn(prev, k)
    const v = had ? share(prev[k], next[k]) : next[k]
    if (!had || !Object.is(v, prev[k])) same = false
    return [k, v]
  })
  // fromEntries 는 키를 자기 속성으로 만든다 — 응답의 `__proto__` 키가 대입처럼 프로토타입을 바꾸지 않는다.
  return same ? prev : Object.fromEntries(out)
}

function shareArray(prev: unknown[], next: unknown[]): unknown[] {
  const byId = new Map<string | number, unknown>()
  const byLease = new Map<string | number, unknown>()
  for (const p of prev) {
    const id = keyOf(p, 'id')
    if (id !== undefined) {
      if (!byId.has(id)) byId.set(id, p)
      continue
    }
    const lease = keyOf(p, 'lease_id')
    if (lease !== undefined && !byLease.has(lease)) byLease.set(lease, p)
  }
  let same = prev.length === next.length
  const out = next.map((n, i) => {
    const id = keyOf(n, 'id')
    const lease = id === undefined ? keyOf(n, 'lease_id') : undefined
    let v = n
    if (id !== undefined) {
      if (byId.has(id)) v = share(byId.get(id), n)
    } else if (lease !== undefined) {
      if (byLease.has(lease)) v = share(byLease.get(lease), n)
    } else if (i < prev.length) {
      v = share(prev[i], n)
    }
    if (i >= prev.length || !Object.is(v, prev[i])) same = false
    return v
  })
  return same ? prev : out
}

function share(prev: unknown, next: unknown): unknown {
  if (Object.is(prev, next)) return prev
  if (Array.isArray(prev) && Array.isArray(next)) return shareArray(prev, next)
  if (plain(prev) && plain(next)) return shareObject(prev, next)
  return next
}

/** next 와 깊이 같은 값. 이전 값과 같은 부분은 이전 객체이고, 전부 같으면 prev 그 자체다. */
export function shareEqual<T>(prev: T | null | undefined, next: T): T {
  return share(prev, next) as T
}
